import { useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';

import { StepStatus } from '@/app/components/Modal/StepIcon';
import { useClaimPendingWithdrawal } from '@/app/components/PortfolioPageComponents/hooks/useClaimPendingWithdrawal';
import { PendingWithdrawalRow } from '@/app/components/PortfolioPageComponents/hooks/usePendingWithdrawals';
import { refreshPortfolioWithdrawalsUi } from '@/app/components/PortfolioPageComponents/refreshPortfolioWithdrawalsUi';
import { TransactionCompletedModalTextType } from '@/app/shared/constants/modals';
import { QueryKeys } from '@/app/shared/constants/queryKeys';
import { cn } from '@/app/shared/lib/cn';
import { preloadModals } from '@/app/shared/lib/preloadModals';
import { Button } from '@/app/shared/ui/Button';

const SequentialStepModal = lazy(() => import('@/app/components/Modal/SequentialStepModal'));
const TransactionCompleted = lazy(() => import('@/app/components/Modal/TransactionCompleted'));

interface PendingWithdrawalWithdrawButtonProps {
  row: PendingWithdrawalRow;
  className?: string;
}

const PendingWithdrawalWithdrawButton = ({ row, className }: PendingWithdrawalWithdrawButtonProps) => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const syncedPartialClaimRef = useRef(false);

  const refreshWithdrawUi = useCallback(() => {
    void refreshPortfolioWithdrawalsUi(queryClient);
  }, [queryClient]);

  const refreshRedeemableOnly = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['readContracts'] });
  }, [queryClient]);

  const {
    handleSend,
    steps,
    transactionStatusData,
    txHash,
    errorMessage,
    resetTransactionState,
    refetchEpochInfo,
    isReady,
  } = useClaimPendingWithdrawal({
    trancheAddress: row.trancheAddress,
    vaultAddress: row.vaultAddress,
    epochId: row.epochId,
    amountMzusd: row.amountBN,
    enabled: row.canClaim,
    onStepSuccess: (stepId) => {
      void refetchEpochInfo();
      if (stepId === 'redeem-mzusd') {
        refreshWithdrawUi();
        return;
      }
      refreshRedeemableOnly();
    },
  });

  const { isSuccess: isTxSuccess, isLoading: isProcessing } = transactionStatusData;
  const canWithdraw = row.canClaim && isReady && !isProcessing;
  const isClaimStepDone = steps.some(
    (step) => step.id === 'claim-tranche' && step.status === StepStatus.Success,
  );

  useEffect(() => {
    if (!isTxSuccess) return;

    setIsModalOpen(false);
    setIsSuccessModalOpen(true);
  }, [isTxSuccess]);

  useEffect(() => {
    if (!errorMessage || !isClaimStepDone || syncedPartialClaimRef.current) return;
    syncedPartialClaimRef.current = true;
    void refetchEpochInfo();
    refreshWithdrawUi();
  }, [errorMessage, isClaimStepDone, refetchEpochInfo, refreshWithdrawUi]);

  const handleWithdraw = () => {
    if (!canWithdraw) return;
    syncedPartialClaimRef.current = false;
    preloadModals();
    handleSend();
    setIsModalOpen(true);
  };

  const handleCloseStepper = () => {
    setIsModalOpen(false);
    if (isClaimStepDone && !isTxSuccess) {
      void refetchEpochInfo();
      refreshWithdrawUi();
    }
  };

  const handleCloseSuccessModal = () => {
    resetTransactionState();
    setIsSuccessModalOpen(false);
    refreshWithdrawUi();
    queryClient.invalidateQueries({ queryKey: [QueryKeys.USER_POSITIONS] });
    queryClient.invalidateQueries({ queryKey: [QueryKeys.USER_PORTFOLIO] });
    queryClient.invalidateQueries({ queryKey: [QueryKeys.USER_TRANSACTIONS] });
  };

  return (
    <>
      <Button
        onClick={handleWithdraw}
        disabled={!canWithdraw}
        variant='outline'
        className={cn('h-9 shrink-0 px-4 text-sm', className)}
      >
        Withdraw
      </Button>

      <Suspense fallback={null}>
        <SequentialStepModal
          isOpen={isModalOpen}
          onClose={handleCloseStepper}
          title='Withdraw'
          steps={steps}
          errorMessage={errorMessage}
          onRetry={() => void handleSend()}
        />
      </Suspense>

      <Suspense fallback={null}>
        <TransactionCompleted
          isOpen={isSuccessModalOpen}
          onClose={handleCloseSuccessModal}
          txHash={txHash}
          amount={row.amount === '-' ? '0' : row.amount}
          symbol='USDC'
          isReversed={true}
          textType={TransactionCompletedModalTextType.CLAIM}
        />
      </Suspense>
    </>
  );
};

export default PendingWithdrawalWithdrawButton;
